// Builds a print-ready snapshot from the live HomeIQ context.
//
// The print route (server-rendered via Playwright) consumes this verbatim —
// every field that appears in the 10-page PDF must originate here. Never
// embed React functions, refs, or component instances.
//
// Versioning: bump `schema_version` whenever the layout adds/removes fields
// so historical reports still render correctly.

export const SNAPSHOT_SCHEMA_VERSION = 1;

const num = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);

export function buildSnapshot(ctx) {
    const {
        // user inputs
        rent, salary, bankRule, obligations, downPayment, tenure, rate,
        targetEMI, segment, sba, zone, sortBy, wealthYear,
        // computed
        affordabilityOutputs, loanOutputs, marketOutputs, investmentOutputs,
        homeIQScore,
        // vastu (optional)
        vastuFinal, vastuConfirmedPlan,
    } = ctx;

    // Top markets — best within budget, sorted by growth.
    const decoratedMarkets = marketOutputs?.decorated ?? [];
    const within = decoratedMarkets.filter((m) => m.status === "Within");
    const topMarkets = [...within]
        .sort((a, b) => (b.growth ?? 0) - (a.growth ?? 0))
        .slice(0, 3)
        .map((m) => ({
            id: m.id,
            area: m.area,
            zone: m.zone,
            growth: m.growth,
            metro: m.metro ?? null,
            growth_score: m.growth_score,
            psf_mid: m.psfBands?.mid ?? m.psf ?? null,
            propertyCost: m.propertyCost,
            budget_gap: m.budget_gap,
            rentYield: m.rentYield,
        }));

    const bestMarket = marketOutputs?.best_within
        ? {
              id: marketOutputs.best_within.id,
              area: marketOutputs.best_within.area,
              zone: marketOutputs.best_within.zone,
              growth: marketOutputs.best_within.growth,
              metro: marketOutputs.best_within.metro ?? null,
              propertyCost: marketOutputs.best_within.propertyCost,
              growth_score: marketOutputs.best_within.growth_score,
              roadConnectivity: marketOutputs.best_within.roadConnectivity ?? null,
              context: marketOutputs.best_within.context ?? null,
          }
        : null;

    return {
        schema_version: SNAPSHOT_SCHEMA_VERSION,
        generated_at_iso: new Date().toISOString(),
        inputs: {
            rent: num(rent), salary, bankRule, obligations, downPayment,
            tenure, rate, targetEMI, segment, sba, zone, sortBy, wealthYear,
        },
        homeIQScore: {
            score: homeIQScore?.score ?? 0,
            verdict: homeIQScore?.verdict ?? "Moderate",
            pillars: (homeIQScore?.pillars ?? []).map((p) => ({
                id: p.id, label: p.label, score: p.score,
                weight: p.weight, effectiveWeight: p.effectiveWeight,
                contribution: p.contribution, included: p.included,
            })),
        },
        affordability: {
            score: affordabilityOutputs?.affordability_score ?? 0,
            eligible_loan: affordabilityOutputs?.eligible_loan ?? 0,
            max_affordable_property: affordabilityOutputs?.max_affordable_property ?? 0,
            emi_on_eligible_loan: affordabilityOutputs?.emi_on_eligible_loan ?? 0,
            foir_usage_pct: affordabilityOutputs?.foir_usage_pct ?? 0,
            foir_status: affordabilityOutputs?.foir_status ?? "—",
            segment: affordabilityOutputs?.segment ?? segment,
            is_alert: affordabilityOutputs?.is_affordability_alert ?? false,
            stress_dscr: affordabilityOutputs?.stress_dscr ?? null,
        },
        loan: {
            score: loanOutputs?.loan_score ?? 0,
            tenure, rate,
            principal: loanOutputs?.principal ?? 0,
            total_interest: loanOutputs?.total_interest ?? 0,
            total_outflow: loanOutputs?.total_outflow ?? 0,
            interest_pct: loanOutputs?.interest_pct ?? 0,
            emi_to_salary_pct: loanOutputs?.emi_to_salary_pct ?? 0,
            prepayment: loanOutputs?.prepayment
                ? {
                      months_saved: loanOutputs.prepayment.months_saved,
                      interest_saved: loanOutputs.prepayment.interest_saved,
                      new_total_interest: loanOutputs.prepayment.new_total_interest,
                      lump: loanOutputs.prepayment.lump,
                      frequency: loanOutputs.prepayment.frequency,
                  }
                : null,
        },
        market: {
            score: marketOutputs?.market_score ?? 0,
            within_count: marketOutputs?.within_count ?? 0,
            beyond_count: marketOutputs?.beyond_count ?? 0,
            reachable_pct: marketOutputs?.reachable_pct ?? 0,
            top_markets: topMarkets,
            best_market: bestMarket,
            budget: affordabilityOutputs?.max_affordable_property ?? 0,
        },
        investment: investmentOutputs?.blocked
            ? { blocked: true, blocked_reason: "Rent not entered" }
            : {
                  blocked: false,
                  score: investmentOutputs?.investment_score ?? 0,
                  horizon_years: wealthYear,
                  buy_equity_at_horizon: investmentOutputs?.buyEquityAtHorizon ?? 0,
                  rent_invest_equity_at_horizon: investmentOutputs?.rentInvestEquityAtHorizon ?? 0,
                  buy_vs_rent_breakeven_year: investmentOutputs?.buyVsRentBreakEvenYear ?? null,
                  cost_of_waiting: investmentOutputs?.costOfWaiting ?? null,
                  recommendation: investmentOutputs?.recommendation ?? null,
              },
        vastu: vastuFinal
            ? {
                  overall_score: vastuFinal.overall_score,
                  verdict: vastuFinal.verdict,
                  dimensions: vastuFinal.dimensions ?? [],
                  positives: vastuFinal.positives ?? [],
                  concerns: vastuFinal.concerns ?? [],
                  recommendations: vastuFinal.recommendations ?? [],
                  confirmed_plan: vastuConfirmedPlan ?? null,
              }
            : null,
    };
}
